import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, CheckCheck } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';

const QUESTIONS_COMMERCIAL = [
  { id: 'documents_pris_connaissance', text: 'Je confirme avoir pris connaissance et compris l\'intégralité des documents contractuels qui m\'ont été présentés avant la signature.' },
  { id: 'infos_transmises', text: 'Je reconnais que l\'ensemble des informations m\'a été transmis par écrit et oralement, et que j\'ai eu la possibilité de poser toutes les questions nécessaires avant de m\'engager.' },
  { id: 'presentation_claire', text: 'Je confirme que la présentation commerciale a été claire, complète et conforme à mes attentes.' },
  { id: 'questions_financement', text: 'Le commercial a répondu à toutes mes questions concernant les modalités de financement, les conditions d\'éligibilité et les aides financières disponibles.' },
  { id: 'explication_bon_commande', text: 'J\'ai reçu une explication détaillée sur le bon de commande, les services inclus, ainsi que les conditions générales de vente.' },
  { id: 'financement_transparent', text: 'Je reconnais que le processus de financement m\'a été expliqué de manière transparente et que j\'ai bien compris les délais et conditions associés.' },
  { id: 'professionnalisme', text: 'Je confirme que le commercial a agi avec professionnalisme et a pris le temps de répondre à mes préoccupations sans exercer de pression ni contrainte.' },
  { id: 'signature_connaissance_cause', text: 'Je reconnais avoir signé le bon de commande en toute connaissance de cause, sans pression ni précipitation.' },
  { id: 'engagement_contractuel', text: 'Je reconnais que cette signature constitue un engagement contractuel ferme, sous réserve des droits légaux applicables.' },
  { id: 'infos_legales', text: 'Je certifie avoir reçu toutes les informations légales concernant mon engagement, y compris le droit à l\'information et les conditions de rétractation prévues par la loi.' },
  { id: 'delai_retractation', text: 'Je suis informé(e) que la loi prévoit un délai de rétractation de 14 jours à compter de la signature, lorsque les conditions légales sont réunies.' },
  { id: 'aides_indicatives', text: 'Je reconnais que les aides financières évoquées l\'ont été à titre indicatif et non garanti.' },
  { id: 'eligibilite_aides', text: 'Je comprends que l\'éligibilité aux aides dépend de critères administratifs, techniques et réglementaires extérieurs à l\'entreprise.' },
  { id: 'montants_evolution', text: 'Je reconnais que les montants évoqués peuvent évoluer, être modifiés ou supprimés par les autorités publiques.' },
  { id: 'pas_garantie_chiffree', text: 'Je reconnais que l\'entreprise ne m\'a donné aucune garantie chiffrée sur les économies, la rentabilité ou le retour sur investissement.' },
  { id: 'pas_incitation_surendettement', text: 'Je reconnais que l\'entreprise ne m\'a pas incité(e) à contracter un financement inadapté à ma situation.' },
  { id: 'comprehension_infos', text: 'Je confirme avoir compris l\'ensemble des informations commerciales, techniques et financières.' },
  { id: 'temps_reflexion', text: 'Je déclare avoir eu le temps nécessaire pour réfléchir avant de m\'engager.' },
  { id: 'consentement_libre', text: 'Je confirme que mon consentement est libre, éclairé et sans contrainte.' },
];

const APRES_INSTALLATION = [
  { id: 'installation_conforme', text: 'Je confirme que l\'installation a été réalisée conformément au devis et aux engagements contractuels.' },
  { id: 'installation_complete', text: 'Je reconnais que l\'installation est complète, fonctionnelle et en bon état à la date de signature.' },
  { id: 'equipe_professionnelle', text: 'L\'équipe technique a agi avec professionnalisme et a respecté mon domicile.' },
  { id: 'explications_fonctionnement', text: 'Le fonctionnement des équipements m\'a été expliqué clairement.' },
  { id: 'documents_recus', text: 'J\'ai reçu les documents relatifs à l\'installation (garantie, notices, certificats le cas échéant).' },
  { id: 'pas_defaut', text: 'Aucun défaut apparent n\'a été constaté à la réception.' },
  { id: 'interventions_futures', text: 'Je reconnais que toute intervention future hors garantie pourra faire l\'objet d\'une facturation.' },
  { id: 'obligations_respectees', text: 'Je reconnais que l\'entreprise a respecté ses obligations d\'information, de conseil et de transparence.' },
  { id: 'validation_reception', text: 'Je comprends que cette validation vaut réception des travaux, sous réserve des garanties légales.' },
];

export default function Step2Validation({ data, onNext }) {
  const [formType, setFormType] = useState('commercial');
  const [checkboxes, setCheckboxes] = useState({});

  useEffect(() => {
    if (data) {
      setFormType(data.form_type || 'commercial');
      setCheckboxes(data.checkboxes || {});
    }
  }, [data]);

  const currentItems = formType === 'commercial' ? QUESTIONS_COMMERCIAL : APRES_INSTALLATION;

  const handleCheckAll = () => {
    const allChecked = {};
    currentItems.forEach(item => {
      allChecked[item.id] = true;
    });
    setCheckboxes(allChecked);
    toast.success('Toutes les cases ont été cochées');
  };

  const handleCheckboxChange = (id) => {
    setCheckboxes({ ...checkboxes, [id]: !checkboxes[id] });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const allChecked = currentItems.every(item => checkboxes[item.id]);
    
    if (!allChecked) {
      toast.error('Veuillez cocher toutes les cases pour continuer');
      return;
    }

    const now = new Date();
    toast.success('Étape 2 validée');
    onNext({
      form_type: formType,
      checkboxes: checkboxes,
      validated_at: now.toISOString(),
      timestamp: now.toLocaleString('fr-FR')
    });
  };

  const checkedCount = Object.keys(checkboxes).filter(k => checkboxes[k]).length;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-2">
          <CheckCircle className="w-8 h-8 text-accent" strokeWidth={1.5} />
        </div>
        <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Formulaire de validation
        </h2>
        <p className="text-base text-foreground/80">
          Confirmez votre situation en quelques clics
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h3 className="font-semibold text-base">Cette validation concerne :</h3>
          
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer p-3 border-2 rounded-lg transition-all hover:bg-accent/5" 
              style={{ borderColor: formType === 'commercial' ? 'hsl(32, 95%, 50%)' : 'hsl(215, 15%, 85%)' }}>
              <input
                data-testid="radio-commercial"
                type="radio"
                name="formType"
                value="commercial"
                checked={formType === 'commercial'}
                onChange={(e) => {
                  setFormType(e.target.value);
                  setCheckboxes({});
                }}
                className="w-5 h-5 mt-0.5 text-accent focus:ring-accent"
              />
              <div className="flex-1">
                <span className="font-medium block">Questions Commercial</span>
                <span className="text-sm text-foreground/60">Validation après signature du bon de commande</span>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer p-3 border-2 rounded-lg transition-all hover:bg-accent/5"
              style={{ borderColor: formType === 'installation' ? 'hsl(32, 95%, 50%)' : 'hsl(215, 15%, 85%)' }}>
              <input
                data-testid="radio-installation"
                type="radio"
                name="formType"
                value="installation"
                checked={formType === 'installation'}
                onChange={(e) => {
                  setFormType(e.target.value);
                  setCheckboxes({});
                }}
                className="w-5 h-5 mt-0.5 text-accent focus:ring-accent"
              />
              <div className="flex-1">
                <span className="font-medium block">Après Installation</span>
                <span className="text-sm text-foreground/60">Validation après réalisation des travaux</span>
              </div>
            </label>
          </div>
        </div>

        <button
          type="button"
          data-testid="check-all-btn"
          onClick={handleCheckAll}
          className="w-full h-14 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 flex items-center justify-center gap-2 font-medium text-lg shadow-md border-b-2 border-accent-foreground/20"
        >
          <CheckCheck className="w-6 h-6" strokeWidth={2} />
          Tout cocher ({currentItems.length} cases)
        </button>

        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-accent/5 border border-accent/20 rounded-md">
            <AlertCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <p className="text-sm text-foreground/80">
              Pour votre sécurité et celle de l'entreprise, merci de confirmer les points suivants
            </p>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {currentItems.map((item) => (
              <div key={item.id} className="flex items-start gap-3 p-3 border border-border rounded-md hover:bg-accent/5 transition-colors">
                <Checkbox
                  data-testid={`checkbox-${item.id}`}
                  id={item.id}
                  checked={checkboxes[item.id] || false}
                  onCheckedChange={() => handleCheckboxChange(item.id)}
                  className="mt-1"
                />
                <Label
                  htmlFor={item.id}
                  className="text-sm leading-relaxed cursor-pointer flex-1"
                >
                  {item.text}
                </Label>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-border">
            <p className="text-sm text-foreground/60">
              {checkedCount} / {currentItems.length} cases cochées
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-foreground/80">
          <p className="font-medium mb-2">📋 Information légale</p>
          <p>
            Ce formulaire constitue une preuve de votre consentement libre et éclairé. Vos réponses sont horodatées et conservées 
            conformément au RGPD. Cette validation ne modifie en rien vos droits légaux, notamment votre droit de rétractation de 14 jours.
          </p>
        </div>

        <button
          data-testid="next-step-btn"
          type="submit"
          className="w-full h-14 bg-accent text-accent-foreground text-lg font-medium rounded-md hover:bg-accent/90 border-b-2 border-accent-foreground/20 active:border-b-0 active:translate-y-[2px] transition-all"
        >
          Continuer
        </button>
      </form>
    </div>
  );
}
